import { useMemo, useState } from 'react';
import {
  AlertOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  FieldTimeOutlined,
  OrderedListOutlined,
  PieChartOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  StopOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { AlertTriangle } from 'lucide-react';

import { Bento, DataTable, PageHeader, StatTile, Tile, TileBody, TileHeader } from '../../../components/shared';
import { Button, Empty, Input, Select, Skeleton } from '../../../components/ui';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayGioVN, ngayVN, soVN } from '../../../lib/dinh-dang';
import { luuTep } from '../../../lib/luu-tep';
import { useToast } from '../../../lib/toast';
import { KhongCoQuyen, LoiRpa } from '../components/chung';
import { BadgeLoaiLoi, BadgeMucDo, BadgeNguon, BadgeTrangThaiLuot, useLoiNgan } from '../components/lich-su/nhan';
import { CotTheoNguon, PhanBo, ThanhTheoProject, VanhMucDo } from '../components/su-co/BieuDoSuCo';
import { HopChiTietSuCo } from '../components/su-co/HopChiTietSuCo';
import { MUC_DO_LOI, NGUON_SU_CO_HIEN, QUYEN, TINH_CHAT_LOI } from '../constants';
import { useSuCo } from '../hooks/useLichSu';
import { ngayChoTenTep, taoCsv } from '../nghiep-vu/csv';
import {
  demTheo,
  demTheoLoai,
  gomTheoLoi,
  KHOANG_NGAY,
  locSuCo,
  loaiSuCo,
  mucDoSuCo,
  MUC_DO_SU_CO,
  NGUON_SU_CO,
  nhanDanhMuc,
  projectTrongSuCo,
  theoGio,
  theoNgay,
  theoProject,
  type BoLocSuCo,
  type DongLoiGom,
} from '../nghiep-vu/su-co';
import type { DanhSachSuCo, SuCo } from '../types';

const TONE = 'orange';
const SO_DONG_THOI_GIAN = 15;
const TRANG_DS_LOI = 10;
const TRANG_NHAT_KY = 20;
const BI_HUY = 'aborted';
const LOC_GOC: BoLocSuCo = { soNgay: 30, project: '', mucDo: '', loai: '', nguon: '', tim: '' };

/**
 * Báo cáo lỗi & Sự cố (`/rpa/incidents`, `RpaIncidentsView` của native): sự cố 30 ngày gộp ba nguồn — lỗi Robot báo
 * về (theo danh mục lỗi đã cấu hình), instance lỗi trên OpenCore và lượt FOXAI gửi lệnh rồi thất bại. Máy chủ trả một
 * lần (tối đa 500 dòng mỗi nguồn); lọc, gom, biểu đồ đều ở máy khách như native. Native có thêm bộ lọc nguồn và ô tìm
 * ở đây là bổ sung theo yêu cầu.
 */
export function SuCoView() {
  const { t } = useT();
  const quyen = useModulePermissions(QUYEN);
  const q = useSuCo(quyen.suCoDoc);
  const tieuDe = t('nav.muc.baoCaoLoiSuCo');

  return (
    <div className="space-y-4 p-4 lg:p-6">
      <PageHeader
        breadcrumb={[{ label: t('rpa.nhom') }, { label: t('nav.muc.quanLyProjectWorkflow') }, { label: tieuDe }]}
        titleIcon={<AlertTriangle size={18} className="text-primary-600" aria-hidden />}
        title={tieuDe}
        subtitle={t('rpaSuCo.moTa')}
        actions={
          quyen.suCoDoc ? (
            <Button
              variant="outline"
              size="md"
              icon={<ReloadOutlined />}
              loading={q.isFetching && !q.isPending}
              onClick={() => void q.refetch()}
            >
              {t('chung.lamMoi')}
            </Button>
          ) : undefined
        }
      />
      {!quyen.suCoDoc ? (
        <KhongCoQuyen quyen={QUYEN.suCoDoc} />
      ) : q.isPending ? (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-10 w-full" />
          <Bento>
            {[0, 1, 2, 3].map((i) => (
              <Tile key={i} span="sm:col-span-6 lg:col-span-3">
                <Skeleton className="h-20 w-full" />
              </Tile>
            ))}
          </Bento>
          <Tile>
            <Skeleton className="h-64 w-full" />
          </Tile>
        </div>
      ) : q.isError ? (
        <Tile>
          <LoiRpa loi={q.error} onThuLai={() => void q.refetch()} />
        </Tile>
      ) : (
        <NoiDung du={q.data} />
      )}
    </div>
  );
}

function NoiDung({ du }: { du: DanhSachSuCo }) {
  const { t, locale } = useT();
  const toast = useToast();
  const loiNgan = useLoiNgan();
  const [loc, setLoc] = useState<BoLocSuCo>(LOC_GOC);
  const [dangXem, setDangXem] = useState<SuCo | null>(null);
  // Mốc "bây giờ" lấy lúc dữ liệu về, để lọc theo ngày không trôi giữa hai lần vẽ.
  const bayGio = useMemo(() => Date.parse(du.meta.since) + 30 * 86_400_000, [du.meta.since]);
  const ds = du.data;
  const projects = useMemo(() => projectTrongSuCo(ds), [ds]);
  const loc1 = useMemo(() => locSuCo(ds, loc, bayGio), [ds, loc, bayGio]);
  const dat = (thay: Partial<BoLocSuCo>) => setLoc((c) => ({ ...c, ...thay }));
  const dangLoc = loc.project !== '' || loc.mucDo !== '' || loc.loai !== '' || loc.nguon !== '' || loc.tim.trim() !== '' || loc.soNgay !== LOC_GOC.soNgay;

  // Gom theo nhãn danh mục, không thì lỗi rút gọn ("Quá thời gian chờ"…): nhiều lượt cùng nguồn nhưng khác lỗi.
  const nhanLoi = (r: SuCo) => nhanDanhMuc(r) || loiNgan(r.message) || loiNgan(r.source);
  const nhanNguon = (r: SuCo) => nhanDanhMuc(r) || loiNgan(r.source);
  const dsLoi = gomTheoLoi(loc1, nhanLoi, t('rpaSuCo.chuaXacDinh'));
  const theoLoai = demTheoLoai(dsLoi);
  const rong = t('rpaSuCo.rongKy');

  const xuatCsv = () => {
    const cot = ['id', 'thoiGian', 'project', 'workflow', 'may', 'nguon', 'maLoi', 'loai', 'nguonLoi', 'mucDo', 'thongBao', 'trangThai'] as const;
    const csv = taoCsv(
      cot.map((k) => t(`rpaSuCo.csv.${k}`)),
      loc1.map((r) => [
        r.id,
        r.createdAt,
        r.projectName || r.projectId,
        r.name || r.workflowId,
        r.machineName,
        t(NGUON_SU_CO_HIEN[r.origin].khoa),
        nhanDanhMuc(r),
        t(TINH_CHAT_LOI[loaiSuCo(r) === 'foreseen' ? 'luongTruoc' : 'batNgo'].khoa),
        r.source,
        t(MUC_DO_LOI[mucDoSuCo(r)].khoa),
        r.message,
        r.status,
      ]),
    );
    luuTep(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `su-co-rpa-${ngayChoTenTep(new Date())}.csv`);
    toast.success(t('rpaSuCo.daXuat', { so: soVN(loc1.length, locale) }));
  };

  if (ds.length === 0) {
    return (
      <>
        <CanhBao du={du} />
        <Tile>
          <Empty icon={SafetyCertificateOutlined} title={t('rpaSuCo.rong')} description={t('rpaSuCo.rongMoTa')} />
        </Tile>
      </>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Input
            allowClear
            prefix={<SearchOutlined aria-hidden />}
            placeholder={t('rpaSuCo.tim')}
            aria-label={t('rpaSuCo.tim')}
            value={loc.tim}
            onChange={(e) => dat({ tim: e.target.value })}
            className="w-full sm:w-64"
          />
          <Select<number>
            value={loc.soNgay}
            onChange={(soNgay) => dat({ soNgay })}
            aria-label={t('rpaSuCo.locKhoang')}
            className="w-32"
            options={KHOANG_NGAY.map((n) => ({ value: n, label: t('rpaSuCo.soNgay', { so: n }) }))}
          />
          <Select<string>
            value={loc.nguon}
            onChange={(nguon) => dat({ nguon })}
            aria-label={t('rpaSuCo.locNguon')}
            className="w-40"
            options={[
              { value: '', label: t('rpaSuCo.moiNguon') },
              ...NGUON_SU_CO.map((n) => ({ value: n, label: t(NGUON_SU_CO_HIEN[n].khoa) })),
            ]}
          />
          <Select<string>
            value={loc.project}
            onChange={(project) => dat({ project })}
            aria-label={t('rpaSuCo.locProject')}
            className="w-52"
            showSearch
            optionFilterProp="label"
            options={[{ value: '', label: t('rpaSuCo.moiProject') }, ...projects.map((p) => ({ value: p.id, label: p.ten }))]}
          />
          <Select<string>
            value={loc.mucDo}
            onChange={(mucDo) => dat({ mucDo })}
            aria-label={t('rpaSuCo.locMucDo')}
            className="w-40"
            options={[
              { value: '', label: t('rpaSuCo.moiMucDo') },
              ...MUC_DO_SU_CO.map((m) => ({ value: m, label: t(MUC_DO_LOI[m].khoa) })),
            ]}
          />
          <Select<string>
            value={loc.loai}
            onChange={(loai) => dat({ loai })}
            aria-label={t('rpaSuCo.locLoai')}
            className="w-44"
            options={[
              { value: '', label: t('rpaSuCo.moiLoai') },
              { value: 'foreseen', label: t(TINH_CHAT_LOI.luongTruoc.khoa) },
              { value: 'unforeseen', label: t(TINH_CHAT_LOI.batNgo.khoa) },
            ]}
          />
          {dangLoc && (
            <Button variant="ghost" size="sm" onClick={() => setLoc(LOC_GOC)}>
              {t('rpaSuCo.boLoc')}
            </Button>
          )}
        </div>
        <Button variant="outline" size="md" icon={<DownloadOutlined />} disabled={loc1.length === 0} onClick={xuatCsv}>
          {t('rpaSuCo.xuatCsv')}
        </Button>
      </div>
      <CanhBao du={du} />

      <Bento>
        <StatTile
          span="sm:col-span-6 lg:col-span-3"
          nhan={t('rpaSuCo.kpiTong')}
          so={soVN(loc1.length, locale)}
          icon={<AlertOutlined />}
          tone="danger"
        />
        <StatTile
          span="sm:col-span-6 lg:col-span-3"
          nhan={t('rpaSuCo.kpiLoi')}
          so={soVN(loc1.filter((r) => r.status !== BI_HUY).length, locale)}
          icon={<WarningOutlined />}
          tone="warning"
        />
        <StatTile
          span="sm:col-span-6 lg:col-span-3"
          nhan={t('rpaSuCo.kpiHuy')}
          so={soVN(loc1.filter((r) => r.status === BI_HUY).length, locale)}
          icon={<StopOutlined />}
          tone="neutral"
        />
        <StatTile
          span="sm:col-span-6 lg:col-span-3"
          nhan={t('rpaSuCo.kpiMttr')}
          so={KHONG_CO}
          sub={t('rpaSuCo.mttrChuaCo')}
          icon={<FieldTimeOutlined />}
          tone="neutral"
        />

        <Tile span="col-span-12 lg:col-span-6">
          <TileHeader icon={<BarChartOutlined />} tone={TONE} title={t('rpaSuCo.theoNguonLoi')} mo={t('rpaSuCo.trongDuLieu')} />
          <TileBody>
            <PhanBo muc={demTheo(loc1, nhanNguon, t('rpaSuCo.chuaXacDinh'))} nhan={t('rpaSuCo.theoNguonLoi')} rong={rong} />
          </TileBody>
        </Tile>
        <Tile span="col-span-12 lg:col-span-6">
          <TileHeader icon={<PieChartOutlined />} tone={TONE} title={t('rpaSuCo.theoMucDo')} mo={t('rpaSuCo.theoMucDoGoiY')} />
          <TileBody>
            <VanhMucDo ds={loc1} nhan={t('rpaSuCo.theoMucDo')} rong={rong} />
          </TileBody>
        </Tile>
        <Tile span="col-span-12 lg:col-span-6">
          <TileHeader icon={<BarChartOutlined />} tone={TONE} title={t('rpaSuCo.theoNgay')} mo={t('rpaSuCo.theoNgayGoiY')} />
          <TileBody>
            <CotTheoNguon
              cot={theoNgay(loc1, loc.soNgay, bayGio)}
              nhan={t('rpaSuCo.theoNgay')}
              rong={rong}
              tieuDeCot={(c) => ngayVN(c.khoa)}
            />
          </TileBody>
        </Tile>
        <Tile span="col-span-12 lg:col-span-6">
          <TileHeader icon={<ClockCircleOutlined />} tone={TONE} title={t('rpaSuCo.theoGio')} />
          <TileBody>
            <CotTheoNguon
              cot={theoGio(loc1)}
              nhan={t('rpaSuCo.theoGio')}
              rong={rong}
              tieuDeCot={(c) =>
                t('rpaSuCo.gio', { gio: c.khoa.padStart(2, '0'), den: String(Number(c.khoa) + 1).padStart(2, '0') })
              }
            />
          </TileBody>
        </Tile>
        <Tile>
          <TileHeader icon={<AppstoreOutlined />} tone={TONE} title={t('rpaSuCo.theoProject')} mo={t('rpaSuCo.trongDuLieu')} />
          <TileBody>
            <ThanhTheoProject
              dong={theoProject(loc1, t('rpaSuCo.chuaCoProject'), t('rpaSuCo.projectKhac'))}
              nhan={t('rpaSuCo.theoProject')}
              rong={rong}
            />
          </TileBody>
        </Tile>
        <Tile className="min-w-0">
          <TileHeader
            icon={<OrderedListOutlined />}
            tone={TONE}
            title={t('rpaSuCo.danhSachLoi')}
            action={
              <span className="text-xs text-content-muted">
                {t('rpaSuCo.tomTatLoai', { luongTruoc: soVN(theoLoai.foreseen, locale), batNgo: soVN(theoLoai.unforeseen, locale) })}
              </span>
            }
          />
          {dsLoi.length === 0 ? (
            <Empty icon={SearchOutlined} title={t('rpaSuCo.rongLoc')} className="py-6" />
          ) : (
            <DataTable<DongLoiGom>
              caption={t('rpaSuCo.danhSachLoi')}
              rowKey="nhan"
              size="small"
              dataSource={dsLoi}
              pagination={dsLoi.length > TRANG_DS_LOI ? { pageSize: TRANG_DS_LOI, showSizeChanger: false } : false}
              columns={[
                { title: t('chung.stt'), key: 'stt', width: 56, render: (_, _d, i) => <span className="tabular-nums">{soVN(i + 1, locale)}</span> },
                {
                  title: t('rpaSuCo.cotLoi'),
                  key: 'nhan',
                  render: (_, d) => (
                    <div className="min-w-0 max-w-md leading-tight">
                      <span className="line-clamp-2 break-words text-content-primary" title={d.nhan}>
                        {d.nhan}
                      </span>
                      {d.nhom && <p className="truncate text-xs text-content-muted">{d.nhom}</p>}
                    </div>
                  ),
                },
                { title: t('rpaSuCo.cotLoai'), key: 'loai', render: (_, d) => <BadgeLoaiLoi loai={d.loai} /> },
                { title: t('rpaSuCo.cotMucDo'), key: 'mucDo', render: (_, d) => <BadgeMucDo mucDo={d.mucDo} /> },
                {
                  title: t('rpaSuCo.cotSoLan'),
                  key: 'soLan',
                  align: 'right',
                  render: (_, d) => <span className="tabular-nums">{soVN(d.soLan, locale)}</span>,
                },
                {
                  title: t('rpaSuCo.cotSoLuong'),
                  key: 'soLuong',
                  align: 'right',
                  render: (_, d) => <span className="tabular-nums">{soVN(d.soLuong, locale)}</span>,
                },
                {
                  title: t('rpaSuCo.cotGanNhat'),
                  key: 'ganNhat',
                  render: (_, d) => <span className="whitespace-nowrap text-xs tabular-nums">{ngayGioVN(d.ganNhat, locale)}</span>,
                },
              ]}
            />
          )}
        </Tile>
        <Tile>
          <TileHeader icon={<ClockCircleOutlined />} tone={TONE} title={t('rpaSuCo.dongThoiGian')} />
          <TileBody className="max-h-96 overflow-y-auto">
            {loc1.length === 0 ? (
              <Empty icon={SearchOutlined} title={t('rpaSuCo.rongLoc')} className="py-6" />
            ) : (
              <ol className="divide-y divide-default">
                {loc1.slice(0, SO_DONG_THOI_GIAN).map((r) => (
                  <li key={`${r.origin}:${r.id}`} className="py-2.5">
                    <small className="tabular-nums text-content-muted">{ngayGioVN(r.createdAt, locale)}</small>
                    <p className="text-sm text-content-primary">{r.name || r.workflowId || KHONG_CO}</p>
                    <p className="text-xs text-content-secondary">{r.message ? loiNgan(r.message) : r.status}</p>
                  </li>
                ))}
              </ol>
            )}
          </TileBody>
        </Tile>
      </Bento>

      <Tile className="min-w-0">
        <TileHeader
          icon={<AlertOutlined />}
          tone={TONE}
          title={t('rpaSuCo.nhatKy')}
          action={<span className="text-xs text-content-muted">{t('rpaSuCo.soSuCo', { so: soVN(loc1.length, locale) })}</span>}
        />
        {loc1.length === 0 ? (
          <Empty icon={SearchOutlined} title={t('rpaSuCo.rongLoc')} className="py-6" />
        ) : (
          <DataTable<SuCo>
            caption={t('rpaSuCo.nhatKy')}
            dataSource={loc1}
            rowKey={(r) => `${r.origin}:${r.id}`}
            size="small"
            pagination={loc1.length > TRANG_NHAT_KY ? { pageSize: TRANG_NHAT_KY, showSizeChanger: false } : false}
            columns={[
              { title: t('chung.stt'), key: 'stt', width: 56, render: (_, _r, i) => <span className="tabular-nums">{soVN(i + 1, locale)}</span> },
              {
                title: t('rpaSuCo.cotThoiDiem'),
                key: 'createdAt',
                width: 150,
                render: (_, r) => <span className="whitespace-nowrap text-xs tabular-nums">{ngayGioVN(r.createdAt, locale)}</span>,
              },
              {
                title: t('rpaSuCo.cotProject'),
                key: 'project',
                render: (_, r) => (
                  <div className="min-w-0 max-w-[12rem] leading-tight">
                    <p className="truncate text-content-primary" title={r.projectName || r.projectId}>
                      {r.projectName || r.projectId || KHONG_CO}
                    </p>
                    {r.projectName && r.projectId && <p className="truncate font-mono text-2xs text-content-muted">{r.projectId}</p>}
                  </div>
                ),
              },
              {
                title: t('rpaSuCo.cotWorkflow'),
                key: 'workflow',
                render: (_, r) => (
                  <span className="block max-w-[12rem] truncate" title={r.name || r.workflowId}>
                    {r.name || r.workflowId || KHONG_CO}
                  </span>
                ),
              },
              {
                title: t('rpaSuCo.cotNguon'),
                key: 'nguon',
                render: (_, r) => (
                  <div className="flex min-w-0 max-w-[12rem] flex-col items-start gap-0.5">
                    <BadgeNguon nguon={r.origin} />
                    <span className="block w-full truncate text-xs text-content-secondary" title={r.errorGroup ? `${nhanNguon(r)} — ${r.errorGroup}` : r.source}>
                      {nhanNguon(r) || KHONG_CO}
                    </span>
                  </div>
                ),
              },
              { title: t('rpaSuCo.cotMucDo'), key: 'mucDo', render: (_, r) => <BadgeMucDo mucDo={mucDoSuCo(r)} /> },
              { title: t('rpaSuCo.cotLoai'), key: 'loai', render: (_, r) => <BadgeLoaiLoi loai={loaiSuCo(r)} /> },
              {
                title: t('rpaSuCo.cotThongBao'),
                key: 'message',
                render: (_, r) => (
                  <span className="line-clamp-2 min-w-[12rem] max-w-sm text-sm text-danger-700" title={r.message}>
                    {r.message ? loiNgan(r.message) : t('rpaSuCo.khongCoThongBao')}
                  </span>
                ),
              },
              { title: t('rpaSuCo.cotTrangThai'), key: 'status', render: (_, r) => <BadgeTrangThaiLuot status={r.status} /> },
              {
                title: <span className="sr-only">{t('chung.thaoTac')}</span>,
                key: 'thaoTac',
                width: 64,
                fixed: 'right',
                align: 'center',
                // Một hành động duy nhất ở mọi dòng: nút icon trần, không menu ⋮ (ghi chú của `MenuThaoTac`).
                render: (_, r) => (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<EyeOutlined />}
                    aria-label={t('rpaSuCo.xemChiTietCua', { ten: r.name || r.workflowId || r.id })}
                    title={t('rpaSuCo.chiTietTieuDe')}
                    onClick={() => setDangXem(r)}
                  />
                ),
              },
            ]}
          />
        )}
      </Tile>

      {dangXem && <HopChiTietSuCo suCo={dangXem} onClose={() => setDangXem(null)} />}
    </>
  );
}

/** Số đã tải, cảnh báo chạm giới hạn và OpenCore không đọc được (vẫn còn hai nguồn kia). */
function CanhBao({ du }: { du: DanhSachSuCo }) {
  const { t, locale } = useT();
  return (
    <div className="space-y-1">
      <p role="status" className="text-xs text-content-muted">
        {t('rpaSuCo.daTai', { so: soVN(du.data.length, locale) })}
        {du.meta.possiblyTruncated && ` ${t('rpaSuCo.biCat')}`}
      </p>
      {du.meta.opencoreError && (
        <p role="alert" className="text-xs text-warning-700">
          {t('rpaSuCo.opencoreLoi')} {du.meta.opencoreError}
        </p>
      )}
    </div>
  );
}
